// @givmo/board — the officer's in-meeting record for one meeting.
//
// Three panels over one meeting: who was in the room, how the evening was shaped, and what the board
// moved. The minutes themselves are the next screen (`/meetings/$meetingId/minutes`) — they are a
// board RECORD, and this is the operational layer underneath them.
//
// ══ THE MUTABILITY BOUNDARY, WHICH IS WHY `readOnly` IS THREADED THROUGH EVERY PANEL ═══════════
//
// These tables are OPERATIONAL WORKING CONTENT until the board approves the minutes: the secretary is
// writing down what happened, live or afterwards, and a mistyped tally in minutes nobody has approved
// yet is just a mistyped tally. **From the approval on, the whole meeting closes** — at the service
// and at six database triggers.
//
// The reason is §3.3's immutability and it is worth stating exactly: from approval, the minutes
// record's `payload` carries a SNAPSHOT of these rows INSIDE the record core's content hash. A row
// that could still move would make the approved minutes disagree with the record they were built
// from. So freezing the substrate at the approval event is what makes "the minutes say what the
// underlying record says" true forever.
//
// The UI's job is to make that legible BEFORE a click rather than as a trigger's refusal after one:
// with the minutes approved, every write affordance is ABSENT (not disabled — the packet's precedent,
// because a greyed control invites a support question with no good answer) and the page says the
// meeting is closed and where a change goes instead.
//
// ⚠ A motion's WORDING freezes EARLIER, at its own vote, independently of any of this — see
// `motions-panel.tsx`. So a meeting can be open for editing while some of its motions are not.

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '../../board-routing';

import { extractList } from '@givmo/api-client/list';
import type { OrgBoardMemberOut } from '@givmo/api-client';
import { Badge, Button, Card, CardBody, Skeleton } from '@givmo/design-system';
import { PageShell } from '../../components/page-shell';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { sortSeats } from '../roster/roster-decode';
import { AttendancePanel } from './attendance-panel';
import { SessionsPanel } from './sessions-panel';
import { ConflictsPanel } from './conflicts-panel';
import { MotionsPanel } from './motions-panel';
import { motionApprovalTarget, useMotionApprovalWords } from './motion-approval';
import { extractSegments, meetingDayOf } from './in-meeting-decode';
import { isMinutesAbsence, isTheRecord } from '../minutes/minutes-decode';
import { useBoardOperations } from '../../board-runtime';
import { MeetingFrame } from '../meetings/meeting-frame';

export function InMeetingRecordPage() {
  const operations = useBoardOperations();
  const { meetingId } = useParams({ from: '/authenticated/meetings/$meetingId/record' });
  const { orgRef, isReady } = useBoardHost();
  const org = orgRef ?? '';
  const enabled = isReady && orgRef !== null;
  // Recording what happened — attendance, sessions, the conflicts named, motions and votes — is
  // `board_meeting:write`. Without it the panels withdraw their affordances exactly as they do for a
  // closed meeting, and the page says why once.
  const canWrite = useBoardPermission('board_meeting:write');
  // CBG-RC11 (C5): arriving from an earlier meeting's minutes with "Approve them at the … meeting",
  // the motion dialog opens in its approval mode, naming those minutes by public facts only.
  const navigate = useNavigate();
  const approvalTarget = motionApprovalTarget(useSearch());
  const approval = useMotionApprovalWords(orgRef ?? '', approvalTarget);

  const meetingQuery = useQuery({
    ...operations.officerGetBoardMeetingOptions({
      path: { org_ref: org, meeting_id: meetingId },
    }),
    enabled,
  });

  // The minutes read is what decides whether this meeting is closed. It is deliberately the SAME
  // signal the backend's guards use (the minutes record being approved), rather than a second notion
  // of "closed" this page could compute and then disagree about.
  const minutesQuery = useQuery({
    ...operations.officerGetBoardMinutesOptions({
      path: { org_ref: org, meeting_id: meetingId },
    }),
    enabled,
    // Minutes may not exist yet, which is the ordinary state before a draft is opened, and is not a
    // failure worth retrying at.
    retry: false,
  });

  // The WHOLE roster, former seats included (CBG-FIX41): a name a recorded fact carries resolves
  // whoever it was, and each panel offers the seats that served on the meeting's day from it.
  const rosterQuery = useQuery({
    ...operations.officerListBoardMembersOptions({
      path: { org_ref: org },
      query: { include_removed: true },
    }),
    enabled,
    select: (body: unknown) => sortSeats(extractList<OrgBoardMemberOut>(body, 'data')),
  });

  const segmentsQuery = useQuery({
    ...operations.officerListBoardMeetingSegmentsOptions({
      path: { org_ref: org, meeting_id: meetingId },
    }),
    enabled,
    select: extractSegments,
  });

  const meeting = meetingQuery.data ?? null;
  // CBG-YR6-MEET: at a committee's meeting a vote is a voting member's of the committee, director or
  // not (the backend's cast gate, `may_cast`), so the vote dialog reads the committee.
  const committeeQuery = useQuery({
    ...operations.officerGetBoardCommitteeOptions({
      path: { org_ref: org, committee_id: meeting?.committee_id ?? '' },
    }),
    enabled: enabled && Boolean(meeting?.committee_id),
  });
  const roster = rosterQuery.data ?? [];
  const servedOn = meetingDayOf(meeting);
  const segments = segmentsQuery.data ?? [];
  const closed = isTheRecord(minutesQuery.data ?? null);
  // ⚠ FAIL-CLOSED ON A LOAD FAILURE. The minutes read is what tells this page whether the meeting is
  // frozen; a 404 genuinely means "no draft yet" (editable), but any OTHER error means we do not
  // know. Treating unknown as editable shows write affordances the database triggers will refuse and
  // invites an officer to retype a correction into a record that is already sealed, so an unreadable
  // minutes state withdraws the affordances and says why.
  const minutesStateUnknown = minutesQuery.isError && !isMinutesAbsence(minutesQuery.error);
  const readOnly = closed || minutesStateUnknown || !canWrite;

  if (meetingQuery.isPending && enabled) {
    return (
      <PageShell title="Meeting record" testId="page-in-meeting-record">
        <div className="flex flex-col gap-12" data-testid="in-meeting-loading">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </PageShell>
    );
  }

  if (meetingQuery.isError || meeting === null) {
    return (
      <PageShell title="Meeting record" testId="page-in-meeting-record">
        <Card testId="in-meeting-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
              We couldn’t load this meeting.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void meetingQuery.refetch()}
              testId="in-meeting-error-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  // The meeting's shared title block and tabs (CBG-UX1): this page is the "Meeting record" tab, at
  // its own address; the meeting and its minutes are the neighbouring tabs.
  return (
    <MeetingFrame meeting={meeting} tab="record" testId="page-in-meeting-record">
      {minutesStateUnknown ? (
        <Card testId="in-meeting-state-unknown">
          <CardBody className="flex flex-col gap-8">
            <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              We couldn’t check whether this meeting is closed
            </span>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Whether the board has approved these minutes decides whether what is recorded here can
              still be changed, and we could not read that just now. Nothing is editable until it
              loads — reload the page to try again.
            </p>
          </CardBody>
        </Card>
      ) : null}

      {closed ? (
        <Card testId="in-meeting-closed">
          <CardBody className="flex flex-col gap-8">
            <div className="flex flex-wrap items-center gap-8">
              <Badge tone="success">Closed</Badge>
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                The board has approved these minutes
              </span>
            </div>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {/* Says what is true and where a change goes — not "you cannot edit this", which leaves
                  a secretary with a correction to make and nowhere to make it. */}
              Everything recorded about this meeting is now part of the approved record, so it is no
              longer edited here. A change to what the minutes say is recorded as a correction, from
              the minutes themselves.
            </p>
            <Link
              to="/meetings/$meetingId/minutes"
              params={{ meetingId }}
              className="self-start font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
              data-testid="in-meeting-correction-link"
            >
              Go to the minutes
            </Link>
          </CardBody>
        </Card>
      ) : null}

      <AttendancePanel orgRef={org} meetingId={meetingId} servedOn={servedOn} readOnly={readOnly} />

      <SessionsPanel
        orgRef={org}
        meetingId={meetingId}
        roster={roster}
        servedOn={servedOn}
        committeeMeeting={Boolean(meeting.committee_id)}
        readOnly={readOnly}
      />

      {/* ⚠ ABOVE the motions, deliberately. The officer decides which disclosures name this agenda,
          and only then reads them beside the motions they bear on — the reverse order would put the
          prompt after the vote it exists to precede. */}
      <ConflictsPanel
        orgRef={org}
        meetingId={meetingId}
        timeZone={meeting.time_zone ?? null}
        readOnly={readOnly}
      />

      <MotionsPanel
        orgRef={org}
        meetingId={meetingId}
        roster={roster}
        servedOn={servedOn}
        segments={segments}
        readOnly={readOnly}
        timeZone={meeting.time_zone ?? null}
        committee={meeting.committee_id ? (committeeQuery.data ?? null) : null}
        committeeMeeting={Boolean(meeting.committee_id)}
        approving={
          approvalTarget !== null && approval !== null
            ? {
                recordId: approvalTarget.recordId,
                // A read that failed still names them, without their meeting.
                words: approval,
              }
            : null
        }
        // Done or dismissed, the address drops the hand-off, so a reload doesn't reopen it.
        onApprovingClosed={() =>
          void navigate({
            to: '/meetings/$meetingId/record',
            params: { meetingId },
            replace: true,
          })
        }
      />
    </MeetingFrame>
  );
}
